import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { IoChevronBack } from 'react-icons/io5';
import Header from '../../components/Header/Header';
import StatCard from '../../components/StatCard/StatCard';
import SimCarrierDistribution from '../../components/SimCarrierDistribution/SimCarrierDistribution';
import DevicesByDistrict from '../../components/DevicesByDistrict/DevicesByDistrict';
import DataUsage from '../../components/DataUsage/DataUsage';
import MapDevicesPanel from '../../components/MapDevicesPanel/MapDevicesPanel';
import DataTable from '../../components/DataTable/DataTable';
import ExportExcelButton from '../../components/ExportExcelButton/ExportExcelButton';
import { useDistrictData } from '../../hooks/useDistrictData';
import { unslugify, slugify } from '../../data/districtData';
import { ENDPOINTS, timeAgo } from '../../api/api';
import { StatCardSkeleton, SectionSkeleton } from '../../components/Skeleton/Skeleton';
import styles from './District.module.css';

const makeDeviceColumns = (navigate, districtSlug) => [
  {
    key: 'imeiNumber',
    label: 'IMEI',
    render: (v, row) => (
      <span
        onClick={() => navigate(`/district/${districtSlug}/circuit/${slugify(row.province || '')}/device/${v}`)}
        style={{ color: 'var(--color-primary)', fontFamily: 'monospace', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px' }}
      >
        {v}
      </span>
    ),
  },
  { key: 'name',         label: 'Name' },
  { key: 'school',       label: 'School' },
  { key: 'province',     label: 'Circuit' },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'model',        label: 'Model' },
  { key: 'lastReportedTime', label: 'Last seen', render: timeAgo },
];

function District() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const districtName = unslugify(slug);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { loading, devicesLoading, stats = {}, circuits = [], carriers = [], dataUsage = [], devices = [], totalPages = 1, refresh } = useDistrictData(districtName, page, search);

  const handleSearch = (val) => { setSearch(val); setPage(1); };

  const breadcrumbs = [
    { label: 'Overview', path: '/' },
    { label: 'District' },
  ];

  return (
    <>
      <Header breadcrumbs={breadcrumbs} />
      <div className={styles.page}>

        <div className={styles.titleRow}>
          <div className={styles.titleLeft}>
            <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Go back">
              <IoChevronBack size={16} />
            </button>
            <h1 className={styles.pageTitle}>{districtName}</h1>
          </div>
          <div className={styles.titleActions}>
            <RefreshButton onClick={refresh} loading={loading} />
          </div>
        </div>

        <div className={styles.statsGrid}>
          {loading
            ? [0, 1, 2].map((i) => <StatCardSkeleton key={i} />)
            : [
                { label: 'Devices Deployed', value: stats.devices  ?? '—' },
                { label: 'Schools',          value: stats.schools  ?? '—', meta: `in ${districtName}` },
                { label: 'Circuits',         value: stats.circuits ?? '—', meta: 'in this district' },
              ].map((s, i) => <StatCard key={i} {...s} />)}
        </div>

        <div className={styles.sectionWrapper}>
          <div className={styles.sectionTitle}>SIM Carrier Distribution</div>
          {loading ? <SectionSkeleton rows={3} /> : <SimCarrierDistribution carriers={carriers} />}
        </div>

        <div className={styles.twoCol}>
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionTitle}>Devices by Circuit</div>
            {loading ? <SectionSkeleton rows={6} /> : <DevicesByDistrict districts={circuits} linkBasePath={`/district/${slug}/circuit`} />}
          </div>
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionTitle}>Data Usage</div>
            {loading ? <SectionSkeleton rows={4} /> : <DataUsage data={dataUsage} />}
          </div>
        </div>

        <MapDevicesPanel district={districtName} />

        <div className={styles.sectionWrapper}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>Device Details</div>
            <ExportExcelButton
              url={ENDPOINTS.districtDevicesExport(districtName.toUpperCase(), search)}
              fallbackFileName={`${slug}-devices.xlsx`}
              disabled={loading}
            />
          </div>
          <DataTable
            columns={makeDeviceColumns(navigate, slug)}
            rows={devices}
            searchable
            searchPlaceholder="Search device details"
            paginated
            pageSize={10}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            onSearchChange={handleSearch}
            loadingRows={devicesLoading}
          />
        </div>

      </div>
    </>
  );
}

export default District;
